import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { ComponentLogic } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-logic/base/component-logic';
import { IBaseComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/base-component/base-component.interface';
import { TableCellComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/table/table-cell-component';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { isContextMenuEvent, isMetaOrShiftOrCtrlKey } from '@src/utils/key-mouse-event/mouse-event-check';
import { TableEditLogic } from '@src/slide/content-viewer/service/canvas-renderer/entry/pc-edit/logic/table-edit-logic';
import { ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import { TEXT_CHANGE_EVENT } from '@src/slide/application/service/text-ruler-manager/core/event-const';
import { FONT_SCALE } from '@editor/src/page/index/components/editor/config';
import { TableRefreshMenu } from '@src/slide/domain/ui-model/element-modeles/property/menu-property/refresh/table';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';

export class TableCellLogic extends ComponentLogic {
    declare protected component: TableCellComponent;
    declare protected componentOptions: InterfaceFactoryOptions<ITableModel>;

    public constructor(componentOptions: InterfaceFactoryOptions<ITableModel>, component: IBaseComponent) {
        super(componentOptions, component);
    }

    protected dealRowHeightChange() {
        this.componentOptions.model.resizeDisplayRowHeight();
    }

    protected handleEnterEditing(): void {
        window.dispatchEvent(new Event('enterEditing'));
    }

    protected handleExitEditing(): void {
        window.dispatchEvent(new Event('exitEditing'));
    }

    protected handleEditorStyleChange = ({ style = {} } = {}) => {
        if (this.component.cellModel && !this.componentOptions.fromScale) {
            this.component.cellModel.selectionStyles = style;
            if (this.component.textEditor?.isEditing) {
                const { model } = this.componentOptions;
                TableRefreshMenu.caculateItemValues(model);
            }
        }
    };

    protected handleSelectionChange = () => {
        const { textSelectionUndoRedo } = this.componentOptions;
        // 暂存当前的文字选区，用于选区的 undo redo
        textSelectionUndoRedo?.stashSelectionAfter(
            this.component.textEditor!.getSelection(),
            this.component.cellModel.tableModelRef.id,
            {
                row: this.component.cellModel.row,
                col: this.component.cellModel.col,
            },
        );
        // todo: 把logic的类声明改成通用的interface声明
        if (browser.isMobile) {
            // @ts-ignore
            this.logic?.handleSelectionChangeTable?.(this.component.textEditor);
        }
    };

    // eslint-disable-next-line complexity
    protected handleEditorMouseDown = (e) => {
        const { model } = this.componentOptions;
        const selectArray = this.componentOptions.selectionService?.selectedElements?.selectedArray || [];
        // 快捷键复选时 且 不是（只有当前表格处于编辑态）时，都失焦
        if (
            isMetaOrShiftOrCtrlKey(e)
            && !(selectArray.length === 0 || (selectArray.length === 1 && selectArray[0] === model))
        ) {
            this.component.textEditor?.triggerBlur();
        }
        // 选中区内的单元格右键，不进入编辑态
        if ((isContextMenuEvent(e) && model.showSelection
            && model.cellSelectedState(
                this.component.cellModel.row ?? 0,
                this.component.cellModel.col ?? 0,
                model.selectedRange,
            ))) {
            setTimeout(() => {
                if (model.selectedCellRanges.length > 1 || model.showSelection) {
                    // 多选单元格时需要让单元格失焦
                    this.component.textEditor?.triggerBlur();
                }
            }, 50);
        } else {
            if (this.logic) {
                this.logic.hasEditorRegisterDocumentMouseUp = true;
            }
        }
        e.stopPropagation();
    };

    protected handleChange = (editingCellData, fromSlateEditor) => {
        if (fromSlateEditor) {
            const { row, col } = this.component.cellModel;
            this.componentOptions.model.uploadSelectedCellData(editingCellData, row, col);
            window.log.info(`cellupdate-handleChange\n${JSON.stringify(editingCellData)}`);
        }
    };

    protected handleEditorMouseUp = () => {
        if (this.logic) {
            this.logic.hasEditorRegisterDocumentMouseUp = false;
        }

        this.componentOptions.formatBrushManager?.onElementSelectionMouseUp();
    };

    protected beforeEditorValueChange = () => {
        const { textSelectionUndoRedo } = this.componentOptions;
        // 文字有值变更，清空暂存的选区 redo 栈
        textSelectionUndoRedo?.clearRedo();
        // 暂存 editor 数据变更前的文字选区
        textSelectionUndoRedo?.stashSelectionBefore(
            this.component.textEditor!.getSelection(),
            this.component.cellModel.tableModelRef.id,
            {
                row: this.component.cellModel.row,
                col: this.component.cellModel.col,
            },
        );
    };

    protected handleTextChange = ({ height }) => {
        // 触发锁页
        const { textEditorStateService } = this.componentOptions;
        if (textEditorStateService) {
            textEditorStateService.editorContentChangeFlag += 1;
        }
        const textHeight = height / FONT_SCALE;
        window.log.info(`handleTextChange-${textHeight}`);
        window.dispatchEvent(new Event(TEXT_CHANGE_EVENT));
        const displayHeight = Math.round(ptToCanvas(textHeight));
        if ((this.component.cellModel.displayHeight ?? 0) > displayHeight
            || this.component.cellModel.props.style.height < displayHeight) {
            this.component.cellModel.displayHeight = displayHeight;
            this.dealRowHeightChange();
        }
    };

    private get logic() {
        return this.componentOptions.logicService.getLogic(this.componentOptions) as TableEditLogic;
    }
}
